import React, { useEffect, useState } from "react";
import {
  AppBar, Toolbar, CssBaseline, Typography, Box, Menu, MenuItem,
  IconButton, useMediaQuery, Avatar, Button, Chip, Stack
} from "@mui/material";
import { styled } from '@mui/system';
import { Theme } from '@mui/material/styles';
import logo from '../assets/images/pool.jpeg';
import MenuIcon from '@mui/icons-material/Menu';
import theme from "../theme";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import HelpIcon from "@mui/icons-material/Help";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import WifiIcon from '@mui/icons-material/Wifi';
import PaymentsIcon from '@mui/icons-material/Payments';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import { useNavigate } from "react-router-dom";
import { Api } from '../helpers';

const formatCount = (n: number) => n >= 1000 ? `${Math.floor(n / 1000)}K+` : `${n}+`;

const APP_STORE_URL = 'https://apps.apple.com/ke/app/spotpool/id6739995703';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.spotpool&pcampaignid=web_share';

const storeBadgeSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
  bgcolor: '#000',
  border: '1px solid rgba(255,255,255,0.25)',
  borderRadius: 2,
  px: 2.5,
  py: 1,
  textDecoration: 'none',
  transition: 'all 0.2s',
  '&:hover': { bgcolor: '#111', borderColor: 'rgba(255,255,255,0.5)' },
};

interface StyledAppBarProps {
  scrolled: boolean;
  theme: Theme;
}

const StyledAppBar = styled(AppBar)<StyledAppBarProps>(({ theme, scrolled }) => ({
  backgroundColor: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
  backdropFilter: scrolled ? 'blur(12px)' : 'none',
  boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.12)' : 'none',
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.standard,
  }),
}));

const features = [
  { icon: <PhoneAndroidIcon />, label: 'Mobile Controlled' },
  { icon: <WifiIcon />, label: 'Remote Management' },
  { icon: <PaymentsIcon />, label: 'In-App Payments' },
  { icon: <ManageAccountsIcon />, label: 'No Staff Needed' },
];

const nav = [
  { path: "/#home", label: "Home", icon: <HomeIcon /> },
  { path: "/#about", label: "About", icon: <InfoIcon /> },
  { path: "/#pricing", label: "Charges", icon: <MonetizationOnIcon /> },
  { path: "/#faqs", label: "FAQs", icon: <HelpIcon /> },
  { path: "/#contact", label: "Contact", icon: <ContactSupportIcon /> },
];

const Hero = (props: any) => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const [stats, setStats] = useState([
    { value: '—', label: 'Tables Managed' },
    { value: '—', label: 'Games Played' },
    { value: '—', label: 'Countries' },
    { value: '99%', label: 'Uptime' },
  ]);
  const isMobile = useMediaQuery('(max-width:700px)');
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    Api({ controller: 'Users', action: 'getLandingStats' })
      .then((resp: any) => {
        setStats([
          { value: formatCount(resp.tablesManaged || 0), label: 'Tables Managed' },
          { value: formatCount(resp.gamesPlayed || 0), label: 'Games Played' },
          { value: formatCount(resp.countries || 0), label: 'Countries' },
          { value: '99%', label: 'Uptime' },
        ]);
      })
      .catch(() => {});
  }, []);

  return (
    <Box
      id='home'
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #0d1117 0%, #0f1923 50%, #111827 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0,107,70,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(176,252,56,0.08) 0%, transparent 40%)',
        }
      }}
    >
      {/* Navbar */}
      <StyledAppBar position="fixed" theme={theme} scrolled={scrolled} elevation={0}>
        <CssBaseline />
        <Toolbar sx={{ px: { xs: 2, md: 6 }, py: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={logo} sx={{ height: 34, width: 34 }} alt='SpotPool' />
            <Typography variant="h6" sx={{ fontWeight: 700, color: scrolled ? 'primary.main' : 'white' }}>
              SpotPool
            </Typography>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {nav.map((item, i) => (
                <a
                  key={i}
                  href={item.path}
                  onClick={() => setActive(i)}
                  style={{
                    fontSize: '0.95rem',
                    padding: '6px 16px',
                    borderRadius: '9999px',
                    textDecoration: 'none',
                    fontWeight: i === active ? 700 : 400,
                    color: scrolled ? (i === active ? '#006B46' : '#333') : (i === active ? '#b0fc38' : '#fff'),
                    borderBottom: i === active ? '2px solid currentColor' : 'none',
                    transition: 'all 0.2s',
                  }}
                >
                  {item.label}
                </a>
              ))}
              <Button
                variant='contained'
                onClick={() => navigate('/auth')}
                sx={{ ml: 2, borderRadius: 8, textTransform: 'none', fontWeight: 600, px: 3 }}
              >
                Get Started
              </Button>
            </Box>
          )}

          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button variant="contained" onClick={() => navigate('/auth')} sx={{ borderRadius: 8, textTransform: 'none', px: 2 }}>
                Get Started
              </Button>
              <IconButton onClick={() => setMenuOpen(true)} sx={{ color: scrolled ? 'primary.main' : 'white' }}>
                <MenuIcon />
              </IconButton>
              <Menu
                open={isMenuOpen}
                onClose={() => setMenuOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {nav.map((item, i) => (
                  <MenuItem key={i} onClick={() => { setActive(i); setMenuOpen(false); }}>
                    <a href={item.path} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {item.icon} {item.label}
                    </a>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Toolbar>
      </StyledAppBar>

      {/* Hero Content */}
      <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: { xs: 3, md: 10 }, maxWidth: 900 }}>
        <Box
          data-aos="fade-down"
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mb: 4 }}
        >
          <Avatar src={logo} alt='SpotPool' sx={{ height: 84, width: 84, boxShadow: '0 8px 30px rgba(0,0,0,0.35)' }} />
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: '#fff', letterSpacing: 0.3 }}>
            SpotPool
          </Typography>
        </Box>

        <Chip
          label="🎱 The Future of Pool Management"
          data-aos="fade-down"
          sx={{ mb: 3, bgcolor: 'rgba(176,252,56,0.15)', color: '#b0fc38', border: '1px solid rgba(176,252,56,0.4)', fontWeight: 600, fontSize: 13 }}
        />

        <Typography
          variant="h2"
          data-aos="fade-up"
          data-aos-delay="100"
          sx={{
            fontWeight: 800,
            color: '#fff',
            fontSize: { xs: '2.2rem', md: '3.5rem' },
            lineHeight: 1.15,
            mb: 3,
            '& span': { color: '#b0fc38' }
          }}
        >
          Revolutionize Your <span>Pool Table</span> Management
        </Typography>

        <Typography
          variant="h6"
          data-aos="fade-up"
          data-aos-delay="200"
          sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 400, mb: 5, fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.8 }}
        >
          Unlock the future of pool table management with our innovative digital token system.
          No coins, no keys — just seamless, automated fun.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='center' data-aos="fade-up" data-aos-delay="300">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/auth')}
            sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 700, px: 5, py: 1.5, fontSize: '1rem' }}
          >
            Get Started Free
          </Button>
          <Button
            variant="outlined"
            size="large"
            href='/#about'
            sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 600, px: 5, py: 1.5, fontSize: '1rem', borderColor: 'rgba(255,255,255,0.5)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
          >
            Watch Demo
          </Button>
        </Stack>

        {/* Feature Pills */}
        <Box data-aos="fade-up" data-aos-delay="400" sx={{ mt: 6, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          {features.map(({ icon, label }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, px: 2.5, py: 1 }}>
              <Box sx={{ color: '#b0fc38', display: 'flex' }}>{icon}</Box>
              <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        {/* App Store / Play Store Badges */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='center' sx={{ mt: 4 }} data-aos="fade-up" data-aos-delay="500">
          <Box component='a' href={APP_STORE_URL} target='_blank' rel='noopener noreferrer' sx={storeBadgeSx}>
            <AppleIcon sx={{ color: '#fff', fontSize: 28 }} />
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, lineHeight: 1 }}>Download on the</Typography>
              <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>App Store</Typography>
            </Box>
          </Box>
          <Box component='a' href={PLAY_STORE_URL} target='_blank' rel='noopener noreferrer' sx={storeBadgeSx}>
            <AndroidIcon sx={{ color: '#b0fc38', fontSize: 26 }} />
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, lineHeight: 1 }}>Get it on</Typography>
              <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>Google Play</Typography>
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Stats Bar */}
      <Box
        data-aos="fade-up"
        data-aos-delay="500"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          bgcolor: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          py: 2.5,
          gap: { xs: 3, md: 0 }
        }}
      >
        {stats.map(({ value, label }, i) => (
          <Box key={label} sx={{ flex: 1, minWidth: 120, textAlign: 'center', borderRight: i < stats.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none', px: 3 }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' }, color: '#b0fc38', lineHeight: 1 }}>{value}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, mt: 0.5 }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Hero;
